// Attorney signatures placed in the signing window. Each is an Apryse stamp
// annotation showing the signature image, tagged with whose signature it is.
// On Sign the stamps are read back as PDF user-space corners (origin
// bottom-left); the server burns in its own copy of the image, so the picture
// here is only a preview.
//
// Annotation coordinates are in the page's unrotated frame, so on a rotated
// page (common on scans) the stamp's width and height are swapped and it is
// turned with the page to read upright, matching what the server burns in.

export const SIGNATURE_DATA_KEY = 'whgAttorneySignature';
// A date box (Matthew, 2026-10-06): today's date, dragged on like a
// signature. The server writes the text itself, sized to the box.
export const DATE_DATA_KEY = 'whgAttorneyDate';
export const DEFAULT_SIGNATURE_WIDTH = 150;
export const DEFAULT_DATE_SIZE = { width: 96, height: 20 };

const round = (value) => Math.round(Number(value) * 100) / 100;

// Width and height as the attorney sees the signature.
export const signatureSize = (attorney, width = DEFAULT_SIGNATURE_WIDTH) => {
  const ratio = attorney?.width && attorney?.height ? attorney.height / attorney.width : 1 / 3;
  return { width, height: round(Math.max(12, width * ratio)) };
};

// Today as the attorney's own calendar shows it, in the form the server stamps.
export const todayText = (now = new Date()) => {
  const two = (n) => String(n).padStart(2, '0');
  return `${two(now.getMonth() + 1)}/${two(now.getDate())}/${now.getFullYear()}`;
};

const BLANK_PNG = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

// A preview picture of the date for the stamp; the server draws the real one.
export const dateImageDataUrl = (text, { width, height } = DEFAULT_DATE_SIZE) => {
  if (typeof document === 'undefined' || !document.createElement) return BLANK_PNG;
  const scale = 4;
  const canvas = document.createElement('canvas');
  canvas.width = width * scale;
  canvas.height = height * scale;
  const ctx = canvas.getContext?.('2d');
  if (!ctx) return BLANK_PNG;
  ctx.fillStyle = '#111827';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  let size = height * 0.57 * scale;
  ctx.font = `${size}px Helvetica, Arial, sans-serif`;
  while (size > 4 * scale && ctx.measureText(text).width > canvas.width - 2 * scale) {
    size -= 0.5 * scale;
    ctx.font = `${size}px Helvetica, Arial, sans-serif`;
  }
  ctx.fillText(text, canvas.width / 2, canvas.height / 2);
  return canvas.toDataURL('image/png');
};

const pageFrame = (instance, pageNumber) => {
  const doc = instance.Core.documentViewer.getDocument();
  const info = doc?.getPageInfo?.(pageNumber) || { width: 612, height: 792 };
  const rotation = (((doc?.getPageRotation?.(pageNumber) || 0) % 360) + 360) % 360;
  const sideways = rotation === 90 || rotation === 270;
  return {
    rotation,
    sideways,
    width: sideways ? info.height : info.width,
    height: sideways ? info.width : info.height,
  };
};

const frameSize = (frame, size) => (frame.sideways ? { width: size.height, height: size.width } : size);

// A box of ``size`` centred on a page point (unrotated frame), kept inside the page.
const boxPlacementAt = (instance, size, { pageNumber, x, y }) => {
  const frame = pageFrame(instance, pageNumber);
  const { width, height } = frameSize(frame, size);
  const clamp = (value, max) => Math.min(Math.max(0, value), Math.max(0, max));
  return {
    pageNumber,
    width,
    height,
    x: clamp(x - width / 2, frame.width - width),
    y: clamp(y - height / 2, frame.height - height),
  };
};

export const signaturePlacementAt = (instance, attorney, point) =>
  boxPlacementAt(instance, signatureSize(attorney), point);

export const datePlacementAt = (instance, point) => boxPlacementAt(instance, DEFAULT_DATE_SIZE, point);

// ``placement`` is in the unrotated frame; without one the box goes in the
// middle of the current page.
const createStamp = async (instance, { size, imageDataUrl, dataKey, dataValue }, placement = null) => {
  const { Annotations, annotationManager, documentViewer } = instance.Core;
  const pageNumber = placement?.pageNumber || documentViewer.getCurrentPage?.() || 1;
  const frame = pageFrame(instance, pageNumber);
  const framed = frameSize(frame, size);

  const annotation = new Annotations.StampAnnotation();
  annotation.PageNumber = pageNumber;
  annotation.Width = placement?.width || framed.width;
  annotation.Height = placement?.height || framed.height;
  annotation.X = placement?.x ?? Math.max(0, frame.width / 2 - annotation.Width / 2);
  annotation.Y = placement?.y ?? Math.max(0, frame.height / 2 - annotation.Height / 2);
  annotation.Rotation = frame.rotation;
  annotation.disableRotationControl?.();
  annotation.MaintainAspectRatio = true;
  annotation.Author = annotationManager.getCurrentUser?.() || annotation.Author;
  annotation.setCustomData(dataKey, dataValue);
  await annotation.setImageData(imageDataUrl);
  annotationManager.addAnnotation(annotation);
  annotationManager.redrawAnnotation(annotation);
  return annotation;
};

export const createSignatureStamp = (instance, attorney, placement = null) =>
  createStamp(
    instance,
    {
      size: signatureSize(attorney),
      imageDataUrl: attorney.image_data_url,
      dataKey: SIGNATURE_DATA_KEY,
      dataValue: attorney.attorney_name,
    },
    placement,
  );

export const createDateStamp = (instance, placement = null, text = todayText()) =>
  createStamp(
    instance,
    {
      size: DEFAULT_DATE_SIZE,
      imageDataUrl: dateImageDataUrl(text),
      dataKey: DATE_DATA_KEY,
      dataValue: text,
    },
    placement,
  );

export const signatureOwner = (annotation) => annotation?.getCustomData?.(SIGNATURE_DATA_KEY) || '';
export const dateText = (annotation) => annotation?.getCustomData?.(DATE_DATA_KEY) || '';
export const isDateStamp = (annotation) => !signatureOwner(annotation) && Boolean(dateText(annotation));

// What the side panel calls a placed box.
export const stampLabel = (annotation) =>
  (isDateStamp(annotation) ? `Date ${dateText(annotation)}` : signatureOwner(annotation));

// Every signature and date box placed in the window, in page order of adding.
export const placedSignatures = (instance) =>
  (instance?.Core?.annotationManager?.getAnnotationsList?.() || []).filter(
    (a) => Boolean(signatureOwner(a)) || isDateStamp(a),
  );

export const readSignaturePlacements = (instance) => {
  const doc = instance?.Core?.documentViewer?.getDocument?.();
  if (!doc) return [];
  return placedSignatures(instance).map((annotation) => {
    const page = annotation.PageNumber;
    const topLeft = doc.getPDFCoordinates(page, annotation.X, annotation.Y);
    const bottomRight = doc.getPDFCoordinates(page, annotation.X + annotation.Width, annotation.Y + annotation.Height);
    const corners = {
      page_number: page,
      x0: round(topLeft.x),
      y0: round(topLeft.y),
      x1: round(bottomRight.x),
      y1: round(bottomRight.y),
    };
    if (isDateStamp(annotation)) return { kind: 'date', text: dateText(annotation), ...corners };
    return { attorney_name: signatureOwner(annotation), ...corners };
  });
};
